/**
 * Credit Alert Card
 * 크레딧 잔액 경고 카드 - 대시보드용
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../../../config/api';

interface CreditInfo {
  balance: number;
  prepaid_enabled?: boolean;
  free_quota: number;
  free_used: number;
  free_remaining: number;
}

// 경고 기준 (테스트용 임시 상향)
const ALERT_THRESHOLD_WARNING = 200000;  // 20만원 이하 - 경고 (원래 1만원)
const ALERT_THRESHOLD_CRITICAL = 50000;  // 5만원 이하 - 긴급 (원래 3천원)

const CreditAlertCard: React.FC = () => {
  const navigate = useNavigate();
  const [credit, setCredit] = useState<CreditInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchCreditBalance();
  }, []);

  const fetchCreditBalance = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token');

      const response = await fetch(`${API_BASE_URL}/api/v1/owner/credits/balance`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('크레딧 정보를 불러올 수 없습니다');
      }

      const result = await response.json();
      if (result.success && result.credit) {
        setCredit(result.credit);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChargeClick = () => {
    navigate('/owner/usage');
  };

  // 디버깅용 로그
  console.log('[CreditAlertCard] loading:', loading, 'error:', error, 'credit:', credit);

  // ⭐ 2026-10-08 — 후불 매장(선불 꺼짐)에는 아무것도 보여주지 않는다.
  //   전 매장이 후불(잔액 0)이라 '크레딧 긴급 부족 · 발송이 중단됩니다'가 매일 떴는데,
  //   후불 매장은 잔액과 무관하게 발송되므로 사실이 아니었다(월말 청구).
  //   로딩·실패 상자도 원장에겐 소음이라 숨긴다(실패는 콘솔에만).
  if (loading || error || !credit) {
    if (error) console.warn('[CreditAlertCard] 크레딧 조회 실패:', error);
    return null;
  }
  if (credit.prepaid_enabled !== true) {
    return null;
  }

  // 잔액이 충분하면 간단한 정보만 표시
  if (credit.balance > ALERT_THRESHOLD_WARNING) {
    return (
      <div className="rounded-lg p-4 mb-4 border-2 bg-green-50 border-green-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <span className="text-xl">✅</span>
            <span className="text-green-700">
              크레딧 잔액: <strong>{credit.balance.toLocaleString()}원</strong>
            </span>
          </div>
          <button
            onClick={handleChargeClick}
            className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700"
          >
            충전하기
          </button>
        </div>
      </div>
    );
  }

  console.log('[CreditAlertCard] 표시 - 잔액 부족:', credit.balance);

  const isCritical = credit.balance <= ALERT_THRESHOLD_CRITICAL;
  const estimatedRemaining = Math.floor(credit.balance / 13); // 13원/건 기준

  return (
    <div
      className={`rounded-lg p-4 mb-4 border-2 ${
        isCritical
          ? 'bg-red-50 border-red-300'
          : 'bg-yellow-50 border-yellow-300'
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-3">
          <span className="text-2xl">
            {isCritical ? '🚨' : '⚠️'}
          </span>
          <div>
            <h3 className={`font-bold text-lg ${
              isCritical ? 'text-red-700' : 'text-yellow-700'
            }`}>
              {isCritical ? '크레딧 긴급 부족!' : '크레딧 잔액 부족'}
            </h3>
            <p className={`text-sm mt-1 ${
              isCritical ? 'text-red-600' : 'text-yellow-600'
            }`}>
              현재 잔액: <span className="font-bold">{credit.balance.toLocaleString()}원</span>
              <span className="text-gray-500 ml-2">
                (약 {estimatedRemaining}건 발송 가능)
              </span>
            </p>
            {isCritical && (
              <p className="text-red-600 text-sm mt-1 font-medium">
                ❌ 크레딧이 소진되면 알림톡 발송이 중단됩니다!
              </p>
            )}
          </div>
        </div>

        <button
          onClick={handleChargeClick}
          className={`px-4 py-2 rounded-lg font-medium text-white transition-colors ${
            isCritical
              ? 'bg-red-600 hover:bg-red-700'
              : 'bg-yellow-600 hover:bg-yellow-700'
          }`}
        >
          💳 충전하기
        </button>
      </div>

      {/* 무료 건수 정보 */}
      {credit.free_quota > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <p className="text-sm text-gray-600">
            📦 이번 달 무료 발송: {credit.free_used}/{credit.free_quota}건 사용
            {credit.free_remaining > 0 && (
              <span className="text-green-600 ml-2">
                ({credit.free_remaining}건 남음)
              </span>
            )}
          </p>
        </div>
      )}
    </div>
  );
};

export default CreditAlertCard;
